import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import { createPinia } from "pinia";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useRecipeWizardStore } from "@/stores/RecipeWizard";
import { clearFoodItems, saveFoodItem } from "@/services/foodItems";
import { clearRecipes, getAllRecipes } from "@/services/recipes";
import {
  getAllDiaryDays,
  patchDiaryDay,
  clearAllDiaryDays,
  type DiaryDayEntry,
} from "@/services/diaryDays";
import { getLoggedDate } from "@/utils/dateTime";
import { getSession } from "@/services/authStore";
import { initializeDeveloperLogging } from "@/services/developerLogging";
import { applyStatusBarStyleForDarkMode } from "@/services/systemBars";
import { applyStoredAppearancePreferences } from "@/services/theme";
import { initSentry } from "@/lib/sentry";
import { initializeDeviceIdentity } from "@/services/deviceIdentity";
import { flushDiaryReplication } from "@/services/rxdb/replication";
import { healthConnectService } from "@/services/healthConnectService";
import {
  syncHealthConnectWeightIn,
  syncHealthConnectNutritionOut,
} from "@/services/healthConnectSync";


/* Tailwind CSS */
import './index.css';

import { defineCustomElements } from '@ionic/pwa-elements/loader';
import VCalendar from 'v-calendar';
import 'v-calendar/style.css';
defineCustomElements(window);

initializeDeveloperLogging();

const appearance = applyStoredAppearancePreferences();
void applyStatusBarStyleForDarkMode(appearance.darkModeEnabled);


type MacroTrackerE2E = {
  recipeWizardSave: () => Promise<{ ok: true } | { ok: false; error: string }>;
  recipeWizardReset: () => void;
  recipeWizardSetYieldCount: (servings: number, label: string) => void;
  recipeWizardSetYieldWeightGrams: (grams: number) => void;
  recipeWizardSetUpdatePreviousEntries: (value: boolean) => void;
  recipesGetAll: () => Promise<
    Array<{ id: string; name: string; isArchived?: boolean; revisionOf?: string }>
  >;
  diaryEntriesGetAll: () => Promise<
    Array<{
      id: string;
      serving: { amount: number; unit: string };
      nutritionSnapshot: { calories: number; protein: number; carbs: number; fat: number };
      source: any;
    }>
  >;
  diaryEntriesSave: (entry: any) => Promise<any>;
  seedClearAll: () => Promise<void>;
  seedFoodSave: (foodItem: any) => Promise<any>;
};

declare global {
  interface Window {
    __macrotrackerE2E?: MacroTrackerE2E;
  }
}

const pinia = createPinia();

const app = createApp(App).use(pinia).use(router).use(VCalendar, {});

initSentry(app, router);

const rootRouteNames = new Set(["tabs-diary", "sign-in", "onboarding"]);

const setupAndroidBackNavigation = (): void => {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") {
    return;
  }

  void CapacitorApp.addListener("backButton", () => {
    const backTarget = window.history.state?.back as string | null | undefined;
    if (backTarget) {
      router.back();
      return;
    }

    const currentRouteName = router.currentRoute.value.name;
    if (!rootRouteNames.has(String(currentRouteName ?? ""))) {
      void router.push({ name: "tabs-diary" });
    }
  });
};

const setupBackgroundDiaryFlush = (): void => {
  void CapacitorApp.addListener("appStateChange", ({ isActive }) => {
    if (isActive) return;
    void getSession().then((session) => {
      if (session?.user.id) flushDiaryReplication(session.user.id);
    });
  });
};

// Health Connect sync. init() refreshes availability/permission state; each sync
// no-ops unless Health Connect is enabled, available, and the matching permission
// is granted. Weight is pulled in on launch/resume; nutrition is pushed out on
// launch and when the app backgrounds (capturing the just-finished session,
// mirroring the diary flush). Writes are idempotent, so overlap is harmless.
const setupHealthConnectSync = (): void => {
  const runWeightIn = async (): Promise<void> => {
    try {
      await healthConnectService.init();
      await syncHealthConnectWeightIn(pinia);
    } catch (error) {
      console.error("Health Connect weight sync failed", error);
    }
  };

  const runNutritionOut = async (): Promise<void> => {
    try {
      await syncHealthConnectNutritionOut();
    } catch (error) {
      console.error("Health Connect nutrition sync failed", error);
    }
  };

  void runWeightIn().then(runNutritionOut);
  void CapacitorApp.addListener("appStateChange", ({ isActive }) => {
    if (isActive) {
      void runWeightIn();
    } else {
      void runNutritionOut();
    }
  });
};

if (import.meta.env.DEV) {
  window.__macrotrackerE2E = {
    recipeWizardSave: async () => {
      const wizard = useRecipeWizardStore(pinia);
      return wizard.save();
    },
    recipeWizardReset: () => {
      const wizard = useRecipeWizardStore(pinia);
      wizard.reset();
    },
    recipeWizardSetYieldCount: (servings: number, label: string) => {
      const wizard = useRecipeWizardStore(pinia);
      wizard.setYieldCount(servings, label);
    },
    recipeWizardSetYieldWeightGrams: (grams: number) => {
      const wizard = useRecipeWizardStore(pinia);
      wizard.setYieldWeightGrams(grams);
    },
    recipeWizardSetUpdatePreviousEntries: (value: boolean) => {
      const wizard = useRecipeWizardStore(pinia);
      wizard.setUpdatePreviousEntries(value);
    },
    recipesGetAll: async () => {
      const recipes = await getAllRecipes();
      return recipes.map((recipe) => ({
        id: recipe.id,
        name: recipe.name,
        isArchived: recipe.isArchived,
        revisionOf: recipe.revisionOf,
      }));
    },
    diaryEntriesGetAll: async () => {
      const days = await getAllDiaryDays();
      return days.flatMap((day) =>
        day.body.meals.flatMap((meal) =>
          meal.entries.map((entry) => ({
            id: entry.id,
            serving: entry.serving,
            nutritionSnapshot: entry.nutritionSnapshot,
            source: entry.source,
          })),
        ),
      );
    },
    diaryEntriesSave: async (entry: any) => {
      const session = await getSession();
      if (!session?.user?.id) {
        throw new Error("[e2e] No authenticated user to save entry.");
      }
      const userId = session.user.id;
      const loggedAt: string = entry.loggedAt ?? new Date().toISOString();
      const date = getLoggedDate(loggedAt);
      await patchDiaryDay(userId, date, (body) => {
        const typed = entry as DiaryDayEntry;
        // e2e harness: drop the entry into an "Unsorted" meal.
        let meal = body.meals.find((m) => m.id === 'unsorted');
        if (!meal) {
          meal = {
            id: 'unsorted',
            mealId: null,
            kind: 'meal',
            planned: { title: 'Unsorted', plannedAt: loggedAt, target: null, exercise: null },
            entries: [],
          };
          body.meals.push(meal);
        }
        const idx = meal.entries.findIndex((e) => e.id === typed.id);
        if (idx === -1) meal.entries.push(typed);
        else meal.entries[idx] = typed;
      });
      return entry;
    },
    seedClearAll: async () => {
      await clearFoodItems();
      await clearRecipes();
      await clearAllDiaryDays();
    },
    seedFoodSave: async (foodItem: any) => {
      return saveFoodItem(foodItem);
    },
  };
}


router.isReady().then(async () => {
  await initializeDeviceIdentity();
  setupAndroidBackNavigation();
  setupBackgroundDiaryFlush();
  setupHealthConnectSync();
  app.mount("#app");
});
